import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Upload, ListChecks, AlertTriangle, SearchX } from 'lucide-react';
import { useWantlistData, useDeckList, useUpdateCardMetadata } from '../hooks/useWantlistData';
import { useWantlistFiltering } from '../hooks/useWantlistFiltering';
import WantlistTable from '../components/WantlistTable';
import WantlistDetailPanel from '../components/WantlistDetailPanel';
import WantlistExportModal from '../components/WantlistExport';
import Page from '../ui/Page';
import PageSplit from '../ui/PageSplit';
import ActionBar from '../ui/ActionBar';
import FilterStrip from '../ui/FilterStrip';
import PageSettingsPopover from '../ui/PageSettingsPopover';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import CardImage from '../ui/CardImage';
import './WantlistPage.css';

// Gruppier-Optionen (Spec: alle 10 bleiben; „Deck" nur im Multi-Deck-Modus)
const GROUP_BY_OPTIONS = [
  { value: 'missing', label: 'Fehlend / Vollständig' },
  { value: 'exportstatus', label: 'Wird Exportiert' },
  { value: 'category', label: 'Kategorie' },
  { value: 'type', label: 'Kartentyp' },
  { value: 'set', label: 'Set' },
  { value: 'friend', label: 'Freund' },
  { value: 'binder', label: 'Binder' },
  { value: 'deck', label: 'Deck' },
  { value: 'label', label: 'Label' },
  { value: 'none', label: 'Keine' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'price', label: 'Preis' },
  { value: 'missing', label: 'Anzahl fehlend' },
];

// Richtungs-Defaults pro Feld (Spec §2.1); Feldwechsel setzt auf Default zurück.
const SORT_DEFAULT_DIRECTION = { name: 'asc', price: 'desc', missing: 'desc' };

export default function WantlistPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // preselectedDeckId kann die UI-ID ("virtual_25") oder die Zahl ("25") sein.
  // Für API-Calls brauchen wir die Zahl, für die Editor-Navigation die UI-ID.
  const preselectedDeckId = searchParams.get('deckId');
  const isSingleDeck = !!preselectedDeckId;
  const preselectedNumericId = (() => {
    if (!preselectedDeckId) return null;
    const match = preselectedDeckId.match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  })();

  const { data: deckListData } = useDeckList();
  const allDecks = useMemo(() => deckListData?.decks || [], [deckListData]);
  const virtualDecks = useMemo(() => allDecks.filter(d => d.source === 'virtual'), [allDecks]);
  const physicalDecks = useMemo(() => allDecks.filter(d => d.source === 'physical'), [allDecks]);

  // selectedDeckIds: bei Single-Deck nur dieses; bei Multi-Deck initial alle virtuellen
  const [selectedDeckIds, setSelectedDeckIds] = useState(
    preselectedNumericId ? [preselectedNumericId] : null
  );

  // Sobald die Deckliste lädt, initialisieren wir Multi-Deck mit allen virtuellen IDs
  useEffect(() => {
    if (!isSingleDeck && selectedDeckIds === null && virtualDecks.length > 0) {
      setSelectedDeckIds(virtualDecks.map(d => d.origid));
    }
  }, [isSingleDeck, selectedDeckIds, virtualDecks]);

  // Auto-gelockte physische Decks: nur sourceBinderIds der TATSÄCHLICH AUSGEWÄHLTEN virtuellen Decks.
  // Wir dürfen nicht alle virtuellen Decks einbeziehen, sonst werden die Quellen anderer Decks
  // mitgelockt, obwohl der User sie gar nicht angeschaut hat.
  const autoLockedSourceIds = useMemo(() => {
    const ids = new Set();
    if (!selectedDeckIds || selectedDeckIds.length === 0) return ids;
    const selectedSet = new Set(selectedDeckIds);
    for (const d of virtualDecks) {
      if (selectedSet.has(d.origid) && d.sourceBinderId) {
        ids.add(d.sourceBinderId);
      }
    }
    return ids;
  }, [virtualDecks, selectedDeckIds]);

  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState('missing');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const handleSortByChange = (field) => {
    setSortBy(field);
    setSortDirection(SORT_DEFAULT_DIRECTION[field] || 'asc');
  };
  const [labelFilter, setLabelFilter] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  // Quellen-Filter (physische Decks): startet mit den auto-gelockten aus Upgrade-Modus
  const [sourcePhysicalDeckIds, setSourcePhysicalDeckIds] = useState([]);
  // Soll der User zusätzliche physische Decks als Quelle nutzen können?
  // Im Upgrade-Modus (autoLockedSourceIds nicht leer) ist die Option implizit
  // immer aktiviert — die verknüpften Decks zählen unabhängig vom Toggle.
  const [includeExternalSources, setIncludeExternalSources] = useState(false);

  useEffect(() => {
    // Wenn die Deckliste gerade geladen wurde, ergänze gelockte Quellen automatisch.
    if (autoLockedSourceIds.size === 0) return;
    setSourcePhysicalDeckIds(prev => {
      const merged = new Set(prev);
      for (const id of autoLockedSourceIds) merged.add(id);
      return [...merged];
    });
    // Upgrade-Mode → Toggle ist implizit aktiv
    setIncludeExternalSources(true);
  }, [autoLockedSourceIds]);

  // Toggle-Handler: beim Deaktivieren werden alle nicht-gelockten Quellen entfernt
  const handleToggleExternalSources = (checked) => {
    setIncludeExternalSources(checked);
    if (!checked) {
      setSourcePhysicalDeckIds([...autoLockedSourceIds]);
    }
  };

  const [exportModal, setExportModal] = useState(null); // { cards, title } | null
  const updateMetadata = useUpdateCardMetadata();

  const { data, isLoading, error, refetch } = useWantlistData(
    selectedDeckIds,
    sourcePhysicalDeckIds,
    isSingleDeck,
  );

  const {
    enrichedCards,
    groupedCards,
    stats,
    toggleCard,
    exportExcluded,
    setExportExcluded,
    exportBasicLands,
    setExportBasicLands,
    hasExcludedCards,
    hasBasicLandCards,
  } = useWantlistFiltering({
    cards: data?.cards || [],
    deckCategories: data?.deckCategories || {},
    searchTerm,
    groupBy,
    sortBy,
    sortDirection,
    labelFilter,
  });

  // Verfügbare Labels aus den geladenen Karten extrahieren (eindeutig)
  const availableLabels = useMemo(() => {
    const seen = new Map();
    for (const c of data?.cards || []) {
      for (const l of c.labels || []) {
        if (!seen.has(l.name)) seen.set(l.name, l);
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [data]);

  // Zahnrad-Indikator: nur NUTZER-veränderte Abweichungen vom Default —
  // die im Upgrade-Modus erzwungenen Quell-Decks zählen nicht (Spec §2).
  const hasSettingsChanges =
    exportBasicLands ||
    exportExcluded ||
    sourcePhysicalDeckIds.some((id) => !autoLockedSourceIds.has(id)) ||
    (includeExternalSources && autoLockedSourceIds.size === 0);

  // selectedCard mit den aktuellen enrichedCards synchron halten.
  // Nach replacePrint hat die Karte eine neue deckCardId — wir suchen anhand
  // (deckId + oracleId) den passenden frischen Eintrag und ersetzen das Objekt.
  // Existiert keiner mehr (Karte gelöscht), wird die Auswahl gelöscht.
  useEffect(() => {
    if (!selectedCard) return;
    const fresh = enrichedCards.find(
      (c) =>
        c.deckId === selectedCard.deckId &&
        c.oracleId === selectedCard.oracleId,
    );
    if (!fresh) {
      setSelectedCard(null);
      return;
    }
    if (fresh.deckCardId !== selectedCard.deckCardId
        || fresh.scryfallId !== selectedCard.scryfallId
        || fresh.printLocked !== selectedCard.printLocked) {
      setSelectedCard(fresh);
    }
  }, [enrichedCards, selectedCard]);

  if (isLoading) {
    return (
      <Page>
        <div className="wantlist-loading" aria-label="Wantlist lädt">
          <Skeleton height="36px" />
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height="40px" />
          ))}
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <EmptyState
          variant="error"
          icon={AlertTriangle}
          title="Wantlist konnte nicht geladen werden"
          description="Klicke auf das Symbol, um es erneut zu versuchen."
          onRetry={() => refetch()}
        />
      </Page>
    );
  }

  return (
    <Page>
      <ActionBar
        left={
          <>
            {isSingleDeck && (
              <ActionBar.Action
                icon={ArrowLeft}
                label="Zurück zum Deck"
                onClick={() => navigate(`/decks/${preselectedDeckId}/editor`)}
              />
            )}
            {/* Reihenfolge = Wichtigkeit; Labels benennen einen Zustand oder Ort,
                damit jeder Wert ohne seine Nachbarn verständlich bleibt. */}
            <ActionBar.Stat
              id="fehlend"
              value={`${stats.totalMissing} / ${stats.totalCards}`}
              label="fehlend"
            />
            <ActionBar.Stat id="export" value={stats.exportCount} label="im Export" />
            <ActionBar.Stat id="wert">{stats.totalValue.toFixed(2)} €</ActionBar.Stat>
            <ActionBar.Stat id="freunde">
              Freunde besitzen <strong>{stats.friendsAvailable}</strong>
            </ActionBar.Stat>
          </>
        }
        right={
          <>
            <PageSettingsPopover hasChanges={hasSettingsChanges}>
              <PageSettingsPopover.Section label="Verfügbarkeit">
                <PageSettingsPopover.Item
                  checked={includeExternalSources}
                  onChange={handleToggleExternalSources}
                  disabled={autoLockedSourceIds.size > 0 || physicalDecks.length === 0}
                  label="Karten aus anderen Decks zählen"
                  description={
                    physicalDecks.length === 0
                      ? 'Du hast keine physischen Decks — sobald du eines anlegst, kannst du seine Karten hier als verfügbar zählen.'
                      : autoLockedSourceIds.size > 0
                        ? 'Fixiert: das geöffnete Deck ist mit einem physischen Deck verknüpft (Upgrade-Modus). Weitere Quell-Decks bleiben wählbar.'
                        : 'Karten in den gewählten physischen Decks gelten als verfügbar — z.B. wenn du planst, ein Deck aufzulösen.'
                  }
                >
                  <PageSettingsPopover.PickerMenu
                    options={physicalDecks.map((d) => ({
                      value: d.origid,
                      label: d.name,
                      locked: autoLockedSourceIds.has(d.origid),
                    }))}
                    value={sourcePhysicalDeckIds}
                    onChange={setSourcePhysicalDeckIds}
                    placeholder="Decks auswählen…"
                  />
                </PageSettingsPopover.Item>
              </PageSettingsPopover.Section>
              <PageSettingsPopover.Section label="Export-Optionen">
                <PageSettingsPopover.Item
                  checked={exportBasicLands}
                  onChange={setExportBasicLands}
                  disabled={!hasBasicLandCards}
                  label="Basic Lands einbeziehen"
                  description={!hasBasicLandCards ? 'Keine Basic Lands in der aktuellen Liste.' : undefined}
                />
                <PageSettingsPopover.Item
                  checked={exportExcluded}
                  onChange={setExportExcluded}
                  disabled={!hasExcludedCards}
                  label="Nicht-im-Deck-Karten einbeziehen"
                  description={!hasExcludedCards ? 'Keine Nicht-im-Deck-Karten in der aktuellen Liste.' : undefined}
                />
              </PageSettingsPopover.Section>
            </PageSettingsPopover>
            <ActionBar.Action
              icon={Upload}
              label="Liste exportieren"
              variant="primary"
              disabled={stats.exportCount === 0}
              onClick={() => setExportModal({ cards: enrichedCards, title: 'Wantlist Export' })}
            />
          </>
        }
      />

      <FilterStrip>
        <FilterStrip.Search
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Suche nach Karte oder Label…"
        />
        {!isSingleDeck && virtualDecks.length > 0 && (
          <FilterStrip.Item id="decks" active={(selectedDeckIds || []).length > 0 && (selectedDeckIds || []).length < virtualDecks.length}>
            <FilterStrip.Group label="Decks">
              <FilterStrip.PillMenu
                mode="multi"
                options={virtualDecks.map((d) => ({ value: d.origid, label: d.name }))}
                value={selectedDeckIds || []}
                onChange={setSelectedDeckIds}
                allLabel="Alle Decks"
                placeholder="Keine Decks"
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
        )}
        {availableLabels.length > 0 && (
          <FilterStrip.Item id="label" active={!!labelFilter}>
            <FilterStrip.Group label="Label">
              <FilterStrip.PillMenu
                mode="single"
                options={[{ value: '', label: 'Alle' }, ...availableLabels.map((l) => ({ value: l.name, label: l.name }))]}
                value={labelFilter ?? ''}
                onChange={(v) => setLabelFilter(v || null)}
                active={!!labelFilter}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
        )}
        <FilterStrip.RightGroup>
          <FilterStrip.Item id="gruppieren">
            <FilterStrip.Group label="Gruppieren">
              <FilterStrip.PillMenu
                mode="single"
                options={GROUP_BY_OPTIONS.filter((o) => o.value !== 'deck' || !isSingleDeck)}
                value={groupBy}
                onChange={setGroupBy}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
          <FilterStrip.Item id="sortieren">
            <FilterStrip.Group label="Sortieren">
              <FilterStrip.PillMenu
                mode="single"
                options={SORT_OPTIONS}
                value={sortBy}
                onChange={handleSortByChange}
              />
              <FilterStrip.SortDirection
                direction={sortDirection}
                onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
        </FilterStrip.RightGroup>
      </FilterStrip>

      {enrichedCards.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="Deine Wantlist ist leer"
          description="Wähle Decks aus oder füge Karten zu deinen Decks hinzu — fehlende Karten erscheinen hier."
        />
      ) : Object.keys(groupedCards).length === 0 ? (
        <EmptyState
          variant="filter"
          icon={SearchX}
          title="Keine Karten für diese Filter"
          description="Suche oder Label-Filter anpassen."
        />
      ) : (
        <PageSplit>
          <WantlistTable
            groupedCards={groupedCards}
            groupBy={groupBy}
            selectedCard={selectedCard}
            onSelectCard={setSelectedCard}
            onToggleCard={(deckCardId) => {
              // Falls die Karte excluded ist, persistieren wir wantlistIncluded.
              // Für nicht-excluded Karten ist der Toggle nur Session-State.
              const card = enrichedCards.find((c) => c.deckCardId === deckCardId);
              if (card?.isExcluded) {
                updateMetadata.mutate({
                  deckCardId,
                  metadata: { wantlistIncluded: !card.wantlistIncluded },
                });
              } else {
                toggleCard(deckCardId);
              }
            }}
            onExportGroup={(groupName, cards) =>
              setExportModal({ cards, title: `Export: ${groupName}` })
            }
          />
          <WantlistDetailPanel
            card={selectedCard}
            onDismiss={() => setSelectedCard(null)}
            onExportForFriend={(friendName) => {
              const friendCards = enrichedCards.filter(
                (c) => c.missing > 0 && (c.friends || []).some((f) => f.name === friendName),
              );
              setExportModal({
                cards: friendCards,
                title: `Bei ${friendName} (${friendCards.length} Karten)`,
              });
            }}
          />
        </PageSplit>
      )}

      {/* Export modal */}
      <WantlistExportModal
        cards={exportModal?.cards || []}
        isOpen={!!exportModal}
        onClose={() => setExportModal(null)}
        title={exportModal?.title}
      />

      {/* Tokens (nur im Single-Deck-Modus) */}
      {isSingleDeck && data?.tokens?.length > 0 && (
        <WantlistTokensSection
          tokens={data.tokens}
          onExport={(missingTokens) => {
            // Tokens als card-like Einträge mappen, damit sie das bestehende
            // Export-Modal nutzen können (dort wird per c.isChecked && c.missing>0 gefiltert).
            const tokenCards = missingTokens.map(t => ({
              name: t.name,
              setCode: null,
              missing: 1,
              isChecked: true,
              price: null,
              quantity: 1,
            }));
            setExportModal({
              cards: tokenCards,
              title: `Tokens (${missingTokens.length})`,
            });
          }}
        />
      )}
    </Page>
  );
}

/**
 * Tokens-Bereich der Wantlist-Seite.
 * Zeigt alle Tokens, die das Deck erzeugt, mit Bild + Besitz-Indikator.
 * Eigener Export-Button (Tokens sind nicht im Gesamt-Export).
 * Keine Deduplizierung — gleichnamige Tokens mit unterschiedlichen
 * Eigenschaften (z. B. Insect mit/ohne Flying) sollen mehrfach erscheinen.
 */
export function WantlistTokensSection({ tokens, onExport }) {
  const missing = useMemo(() => tokens.filter(t => !t.owned), [tokens]);

  return (
    <section className="wantlist-tokens">
      <header className="wantlist-tokens__header">
        <h2 className="wantlist-tokens__title">
          Tokens <span className="wantlist-tokens__count">({tokens.length})</span>
        </h2>
        {missing.length > 0 && (
          <button
            type="button"
            className="btn-secondary wantlist-tokens__export"
            onClick={() => onExport?.(missing)}
            title={`${missing.length} fehlende Tokens exportieren`}
          >
            Fehlende exportieren ({missing.length})
          </button>
        )}
      </header>
      <ul className="wantlist-tokens__list">
        {tokens.map((t, i) => (
          <li
            key={`${t.id}-${i}`}
            className={`wantlist-tokens__item${t.owned ? ' wantlist-tokens__item--owned' : ''}`}
            title={t.name}
          >
            {t.imageUrl ? (
              <CardImage src={t.imageUrl} alt={t.name} />
            ) : (
              <div className="wantlist-tokens__image--placeholder">
                {t.name?.[0] ?? '?'}
              </div>
            )}
            <div className="wantlist-tokens__caption">
              <span className="wantlist-tokens__name">{t.name}</span>
              <span className="wantlist-tokens__status">
                {t.owned ? (
                  <>
                    <Check size={12} aria-hidden /> Besitz
                  </>
                ) : (
                  <>
                    <X size={12} aria-hidden /> Fehlt
                  </>
                )}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
